import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { getWalletPixallMetadata } from 'reaxl-analytics';
import { Google, Pixall, Segment } from '../analytics/index.js';
import { getInventoryOwnerById, cleanValues, transformTileType, isVDP } from '../utilities/index.js';
import formatDataLayerToPixall from '../analytics/pixall/helpers/formatDataLayer';
import formatDataLayerToSegment from '../analytics/segment/helpers/formatDataLayer';
import { DataLayerTransformer, inventoryTransformer as pixallInventoryTransFormer, QueryStringTransformer } from '../transformers/pixall/index.js';
import { inventoryTransformer as googleInventoryTransformer, productConfigTransformer as googleProductConfigTransformer } from '../transformers/google/index.js';
import { valueEventCode } from '../constants/index.js';
const {
  transformProductConfig
} = googleProductConfigTransformer;
const psxSRPBaseAnalyticsObj = {
  listingType: 'pvt',
  pageType: 'srp'
};
const psxVDPBaseAnalyticsObj = {
  listingType: 'pvt',
  pageId: 'fyc_psx_vdp',
  pageType: 'vdp',
  vehicleType: 'USED'
};
const psxBLPBaseAnalyticsObj = {
  eventSource: 'link',
  eventType: 'click',
  pageId: 'syc_psx_buy',
  pageType: 'own',
  program: 'DealerDotCom'
};
const psxTrackingEventPixallBaseAnalyticsObj = {
  'find-your-car': {
    vdp: {
      ...psxVDPBaseAnalyticsObj
    }
  },
  own: {
    psx_chat: {
      listingType: 'pvt',
      pageId: 'psx_chat',
      pageType: 'chat',
      vehicleType: 'USED'
    },
    'psx_offers-detail': {
      listingType: 'pvt',
      eventSource: 'userAccount',
      eventType: 'click',
      pageId: 'psx_offer-details',
      pageType: 'psx',
      program: 'DealerDotCom',
      vehicleType: 'USED'
    }
  },
  srp: {
    srp: {
      listingType: 'pvt',
      pageId: 'fyc_srl',
      pageType: 'srp'
    }
  }
};
function getGoogleEventData(eventResult, category, eventAction, eventData) {
  if (category === void 0) {
    category = 'psx';
  }
  if (eventAction === void 0) {
    eventAction = 'click';
  }
  if (eventData === void 0) {
    eventData = {};
  }
  return {
    event: 'analytics_event',
    eventElement: category,
    eventAction,
    eventResult,
    eventNonInteractive: false,
    ...(!_isEmpty(eventData) && {
      eventData
    })
  };
}
function sendPSXLinkClickEvent(googleEventData, pixallEventData, option, metadata) {
  if (googleEventData && Object.entries(googleEventData || {}).length > 0) {
    Google.sendClickEvent(googleEventData, option);
  }
  if (pixallEventData && Object.entries(pixallEventData || {}).length > 0) {
    const {
      dataLayer = {}
    } = metadata;
    Pixall.sendClickEvent(pixallEventData, dataLayer, option);
  }
  return googleEventData;
}
export function psxAnalyticsEvent(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const data = metadata.data || {};
  const dataLayer = metadata.dataLayer || {};
  const {
    event,
    customData = {},
    eventData = {},
    ...googleData
  } = data;
  const {
    appKey = '',
    pixall,
    segment
  } = customData;
  const listingId = pixall?.id || segment?.id;
  const inventoryWithOwner = getInventoryOwnerById(dataLayer, listingId);
  const isSRP = dataLayer?.page?.pageName === 'srp';
  const isLoggedIn = dataLayer?.user?.isLoggedIn === '1' || dataLayer?.user?.isLoggedIn === 1;
  const isPsxListingActions = appKey === 'psx-listing-actions';
  let vehicleEventDataInSrp = {};
  if (isSRP) {
    vehicleEventDataInSrp = {
      ...googleInventoryTransformer(inventoryWithOwner, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'listing'
    };
  }
  let eventGoogleData = {};
  if (googleData && Object.entries(googleData || {}).length > 0) {
    eventGoogleData = {
      event,
      ...getGoogleEventData(googleData.eventResult, googleData.eventElement, googleData.eventAction),
      eventData: {
        ...eventData,
        vehicle: {
          ...googleInventoryTransformer(dataLayer.vehicle, {
            target: 'vehicle',
            dataLayer
          }),
          ...eventData.vehicle,
          ...vehicleEventDataInSrp
        }
      },
      ...googleData
    };
  }
  if (isSRP && isPsxListingActions && !isLoggedIn) {
    eventGoogleData = {};
  }
  let eventPixallData = {};
  if (pixall) {
    const {
      siteSection,
      pageType
    } = dataLayer.page || {};
    let psxPixallTrackingObject = psxTrackingEventPixallBaseAnalyticsObj[siteSection]?.[pageType];
    psxPixallTrackingObject = psxPixallTrackingObject || psxVDPBaseAnalyticsObj;
    const baseAnalytics = {
      ...psxPixallTrackingObject
    };
    delete baseAnalytics.dealerCode;
    delete baseAnalytics.wspCode;
    eventPixallData = {
      ...formatDataLayerToPixall(dataLayer),
      ...cleanValues(QueryStringTransformer.getSearchFacetVariables()),
      ...baseAnalytics,
      ...pixall,
      ...pixallInventoryTransFormer.getInventoryData(inventoryWithOwner),
      stockNumber: inventoryWithOwner.stockId
    };
  }
  sendPSXLinkClickEvent(eventGoogleData, eventPixallData, option, metadata);
  if (segment) {
    const {
      data: segmentPageData
    } = formatDataLayerToSegment(metadata);
    const {
      eventName: segmentEventName,
      eventType: segmentEventType,
      identifyUserId,
      ...segmentData
    } = segment;
    option.brand = metadata?.dataLayer?.brand || '';
    if (segmentEventType === 'identify') {
      Segment.sendIdentifyEvent(identifyUserId, segmentData);
    } else if (segmentEventType === 'reset') {
      Segment.sendResetEvent();
    } else {
      Segment.sendClickEvent(segmentEventName, {
        ...segmentPageData,
        ...segmentData
      }, option);
    }
  }
  return data;
}
export function psxVideoStart(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const {
    isPsxBlp = false
  } = options;
  const currentPageName = _get(state, 'currentPageName', '');
  let pixallDataObject = {
    listingCode: owner?.id,
    zipCode: dataLayer.user?.zipCode,
    ...data
  };
  if (isPsxBlp) {
    pixallDataObject = {
      ...pixallDataObject,
      ...psxBLPBaseAnalyticsObj,
      eventSource: 'link',
      eventType: 'click',
      label: 'slp_psx_buy::video::start',
      hasListingType: false
    };
    delete pixallDataObject?.vehicleType;
  } else {
    pixallDataObject = {
      ...pixallDataObject,
      ...psxVDPBaseAnalyticsObj,
      eventSource: 'application',
      eventType: 'start',
      label: 'vid_start'
    };
  }
  const ga360Obj = {
    event: 'analytics_event',
    eventElement: 'video',
    eventAction: 'click',
    eventResult: `${currentPageName === 'vdp' ? '' : 'slp_'}psx_buy::video::start`,
    eventNonInteractive: false,
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  return sendPSXLinkClickEvent(ga360Obj, pixallDataObject, options, metadata);
}
export function psxVideoComplete(metadata, option) {
  const {
    data = {},
    dataLayer = {}
  } = metadata;
  const {
    isPsxBlp = false
  } = option;
  const clickObject = {
    event: 'analytics_event',
    category: 'video',
    eventAction: 'view',
    eventResult: 'slp_psx_buy:video::complete',
    eventNonInteractive: false
  };
  let pixallDataObject = {
    label: 'slp_psx_buy:video::complete',
    hasListingType: false,
    ...data
  };
  if (isPsxBlp) {
    pixallDataObject = {
      ...pixallDataObject,
      ...psxBLPBaseAnalyticsObj
    };
  } else {
    pixallDataObject = {
      ...pixallDataObject,
      ...psxVDPBaseAnalyticsObj
    };
  }

  // send video view complete event on its own because it doesn't have the 'click' eventAction like the others
  Google.sendClickEvent(clickObject, option);
  Pixall.sendClickEvent(pixallDataObject, dataLayer, option);
  return clickObject;
}
export function psxPrivateSellerCarsClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::srp:private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::srp:private-seller-cars', 'search-car'), pixallDataObject, option, metadata);
}
export function psxSellMyCarLinkClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::sell-my-car'), pixallDataObject, option, metadata);
}
export function psxPreQualifyClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::pre-qualify'), pixallDataObject, option, metadata);
}
export function psxWarrantyClick(metadata, option) {
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::warranty'), null, option);
}
export function psxEscrowClick(metadata, option) {
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::escrow'), null, option);
}
export function psxAutoGapInsuranceClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::auto-gap-insurance'), pixallDataObject, option, metadata);
}
export function psxVehicleProtectionClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::vehicle-protection'), pixallDataObject, option, metadata);
}
export function psxBuyerGuideClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::buyer-guide'), pixallDataObject, option, metadata);
}
export function psxVDPPopoverCtaClick(metadata, option) {
  const {
    data = {},
    state
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    ...psxVDPBaseAnalyticsObj,
    ...data,
    eventSource: 'link',
    eventType: 'click',
    label: 'text-link::submit-an-offer',
    inventory
  };
  return sendPSXLinkClickEvent({}, pixallDataObject, option, metadata);
}
export function psxSellerGuideClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::seller-guide'), pixallDataObject, option, metadata);
}
export function psxFAQsClick(metadata, option) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    label: 'text-link::private-seller-cars-in-my-area',
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::faqs'), pixallDataObject, option, metadata);
}
export function psxPrivateSellerExchangeLogoClick(metadata, option) {
  const pixallData = {
    label: 'psx_exchange_lgo',
    eventSource: 'button'
  };
  return sendPSXLinkClickEvent(getGoogleEventData('logo::slp_psx::private-seller-exchange'), pixallData, option, metadata);
}
export function psxVDPPageLoadEvent(metadata, options) {
  const {
    data = {},
    dataLayer,
    history
  } = metadata;
  const pixallDataObject = {
    label: 'fyc_psx_vdp',
    pageId: 'fyc_psx_vdp',
    pageType: 'vdp',
    ...data
  };
  Google.sendPageDataEvent(metadata, options);
  Pixall.sendPageEvent({
    pixallDataObject
  }, {
    dataLayer,
    history
  }, options);
  return metadata;
}
export function psxVDPBuyNowClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'sale',
    eventType: 'start',
    label: 'buy_now',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::buy-now', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPTestDriveClick(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId,
    placement
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  let label = 'test_drv';
  if (placement === 'nds') {
    label = `${label}-${placement}`;
  } else if (placement === 'sticky-header' || placement === 'sticky-footer') {
    label = `${label}::${placement}`;
  }
  const pixallDataObject = {
    eventSource: 'test-drive',
    eventType: 'click',
    label,
    inventory,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::open::test-drive', 'private-seller', 'click', {
    eventName: 'Test-Drive',
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      cj_ga_event_value_code: valueEventCode.PSX_TEST_DRIVE
    }
  }), pixallDataObject, options, metadata);
}
export function psxVDPChatSellerClick(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const pixallDataObject = {
    eventSource: 'lead',
    eventType: 'click',
    feature: 'private-seller',
    label: 'cs_chat',
    leadType: 'chat',
    inventory,
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: valueEventCode.PSX_CHAT_SELLER
        }
      }
    },
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('lead::chat', 'private-seller', 'submit', {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      cj_ga_event_value_code: valueEventCode.PSX_CHAT_SELLER
    }
  }), pixallDataObject, options, metadata);
}
export function psxMakeOfferClick(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId,
    placement
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isSRP = dataLayer?.page?.pageName === 'srp';
  const isKBBPageId = inventory.listingType === 'USED' ? 'classlistused' : 'classlistnew';
  const isKBB = state?.brand === 'kbb';
  const isLoggedIn = state?.auth?.isLoggedIn;
  const baseAnalytics = {
    pageId: isSRP && isKBB ? isKBBPageId : 'fyc_srl',
    ...(isSRP ? psxSRPBaseAnalyticsObj : psxVDPBaseAnalyticsObj)
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      cj_ga_event_value_code: valueEventCode.PSX_MAKE_OFFER,
      clickType: 'pvt'
    }
  };
  let label = 'make_off';
  if (placement === 'nds') {
    label = `${label}-${placement}`;
  } else if (placement === 'sticky-header' || placement === 'sticky-footer') {
    label = `${label}::${placement}`;
  }
  const pixallDataObject = {
    eventSource: 'purchase-offer',
    eventType: 'start',
    label,
    inventory,
    ...baseAnalytics,
    vehicleType: (inventory?.listingType || '').toUpperCase(),
    zipCode: dataLayer?.user?.zipCode || '',
    ...data
  };
  return sendPSXLinkClickEvent({
    ...((isLoggedIn || isVDP(dataLayer)) && getGoogleEventData('cta::make-offer', 'private-seller', 'click', {
      ...(isVDP(dataLayer) && eventData)
    }))
  }, pixallDataObject, options, metadata);
}
export function psxVDPGetPreQualifyClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    leadType: 'chat',
    label: 'get_prequal',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::get-pre-qualified', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPBuyerGuideClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'buyer_guide',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::buyer-guide', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPEmailSubscribeClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'button',
    eventType: 'click',
    label: 'subsc_alrt_eml',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::alert::subscribe::email', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPVHRClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {},
    event
  } = metadata;
  const {
    listingId,
    vendor
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pageId = psxVDPBaseAnalyticsObj.pageId || DataLayerTransformer.getPageId(dataLayer);
  let cmp = 'ec_pa_txt';
  if (event.target.tagName?.toLowerCase() === 'img') {
    cmp = 'ec_pa_lgo';
  }
  const productTiles = _get(inventory, 'productTiles', []).filter(tile => tile && tile.tileType !== 'CERTIFIED_CAR');
  const epn = data.epn || _get(productTiles, '[0]epn', '') || transformTileType({
    tileType: _get(productTiles, '[0]tileType', ''),
    isPrivateSeller: true
  });
  const pixallDataObject = {
    eventSource: 'vehicle-history-report',
    eventType: 'open',
    goal: 'vehicle-history-report',
    label: epn ? `${cmp}-${epn}-${pageId}` : `${cmp}-${pageId}`,
    listingCode: inventory?.owner?.id,
    inventory,
    vendor,
    ...(vendor === 'autocheckps' && {
      cdl: {
        eventProperties: {
          event: {
            valueEventCode: 'A369'
          }
        }
      }
    }),
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData(`vehicle-history-report::${vendor ?? 'autocheckn'}`, 'partner', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPVHRImpression(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId,
    vendor
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const zipCode = _get(state, 'query.zip', '');
  const getLabel = () => {
    switch (vendor) {
      case 'carfaxps':
        return 'vehicle-history-report::carfaxvhr';
      case 'autocheckps':
        return 'vehicle-history-report::autocheck';
      default:
        return '';
    }
  };
  const pixallDataObject = {
    eventSource: 'vehicle-history-report',
    eventType: 'impression',
    goal: 'vehicle-history-report',
    label: getLabel(),
    listingCode: inventory?.owner?.id,
    vendor,
    zipCode,
    ...pixallInventoryTransFormer.getInventoryData(inventory),
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const googleData = {
    eventElement: 'partner',
    eventAction: 'impression',
    eventResult: getLabel(),
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  Pixall.sendImpression({
    dataLayer
  }, pixallDataObject, options);
  Google.sendImpressions(metadata, googleData, options);
  return pixallDataObject;
}
export function psxVDPOptionDetailsAccordionClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'vehicle',
    eventType: 'click',
    label: 'v_feat_opt_dtls',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('dropdown::options-details', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPStandardEquipmentAccordionClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'vehicle',
    eventType: 'click',
    label: 'v_feat_stnd_equip',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('dropdown::standard-equipment', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPOtherEquipmentAccordionClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'vehicle',
    eventType: 'click',
    label: 'v_feat_other_equip',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('dropdown::other-equipment', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxVDPEstimateYourMonthlyPaymentClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner,
    make
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'est_mnth_pymt',
    listingCode: owner?.id,
    inventory,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData(`text-link::estimate-your-monthly-payments-for-this-${(data.make || (typeof make === 'string' ? make : make?.name || '')).toLowerCase()}`, 'private-seller', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPVehicleProtectionVehicleServiceContractsClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'lrn_more_v_serv_contr',
    listingCode: inventory?.owner?.id,
    inventory,
    zipCode: dataLayer.user?.zipCode,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::learn-more-about-vehicle-service-contracts', 'private-seller', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPVehicleProtectionGAPProtectionClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'lrn_more_gap_ptect',
    listingCode: inventory?.owner?.id,
    inventory,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::learn-more-about-gap-protection', 'private-seller', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPFaqClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'faq',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::please-read-our-frequently-asked-questions', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxLearnMoreFaqsClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    ...psxBLPBaseAnalyticsObj,
    ...data,
    label: 'lrn_more_faqs'
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::faqs'), pixallDataObject, options, metadata);
}
export function psxLearnMoreBuyersGuideClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    ...psxBLPBaseAnalyticsObj,
    ...data,
    label: 'lrn_more_buyers-guide'
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::buyer-guide'), pixallDataObject, options, metadata);
}
export function psxLearnMoreSellersGuideClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    ...psxBLPBaseAnalyticsObj,
    ...data,
    label: 'lrn_more_sellers-guide'
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::seller-guide'), pixallDataObject, options, metadata);
}
export function psxStripeClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    ...psxVDPBaseAnalyticsObj,
    label: 'text-link::stripe-id-check',
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::stripe-id-check'), pixallDataObject, options, metadata);
}
export function psxVDPFinanceClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'fin_lnk',
    listingCode: owner?.id,
    zipCode: dataLayer.user?.zipCode,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::financing', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPVehicleServiceContractsClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'v_sev_cont_lnk',
    listingCode: owner?.id,
    zipCode: dataLayer.user?.zipCode,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::vehicle-service-contracts', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPGapInsuranceClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'gap_ins',
    listingCode: owner?.id,
    zipCode: dataLayer.user?.zipCode,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::gap-insurance', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxBLPFinanceClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'fin_lnk',
    hasListingType: false,
    listingCode: owner?.id,
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::financing', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxBLPVehicleServiceContractsClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'v_sev_cont_lnk',
    listingCode: owner?.id,
    hasListingType: false,
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::vehicle-service-contracts', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxBLPGapInsuranceClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    owner
  } = inventory;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'gap_ins',
    listingCode: owner?.id,
    hasListingType: false,
    ...psxBLPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('text-link::gap-insurance', 'sell-your-car', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPEstimateDeliveryCostZipSubmit(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'cta_submit-zip-delivery',
    listingCode: inventory?.owner?.id,
    inventory,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::submit-zip-delivery', 'private-seller', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxVDPMyDashboardClick(metadata, options) {
  const {
    data = {}
  } = metadata;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'cta_my-dashboard',
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::my-dashboard', 'private-seller'), pixallDataObject, options, metadata);
}
export function psxOptionAddOnProtectionsImpression(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const googleData = {
    eventElement: 'private-seller',
    eventAction: 'impression',
    eventResult: 'cta::view-more-information::additional-protections',
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  Google.sendImpressions(metadata, googleData, options);
  return googleData;
}
export function psxOptionAddOnProtectionsClick(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const googleData = {
    eventElement: 'private-seller',
    eventAction: 'click',
    eventResult: 'cta::view-more-information::additional-protections',
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  Google.sendClickEvent(googleData, options);
  return googleData;
}
export function psxManufactureWarrantyImpression(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const googleData = {
    eventElement: 'private-seller',
    eventAction: 'impression',
    eventResult: 'cta::see-details::manufacture-warranty',
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  Google.sendImpressions(metadata, googleData, options);
  return googleData;
}
export function psxManufactureWarrantyClick(metadata, options) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const googleData = {
    eventElement: 'private-seller',
    eventAction: 'click',
    eventResult: 'cta::see-details::manufacture-warranty',
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  Google.sendClickEvent(googleData, options);
  return googleData;
}
export function psxEmailUsClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    eventSource: 'button',
    eventType: 'click',
    label: 'cta::email-us',
    listingCode: inventory?.owner?.id,
    inventory,
    zipCode: dataLayer.user?.zipCode,
    ...psxVDPBaseAnalyticsObj,
    ...data
  };
  const eventData = {
    vehicle: {
      ...googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      }),
      clickType: 'pvt'
    }
  };
  return sendPSXLinkClickEvent(getGoogleEventData('cta::email-us', 'private-seller', 'click', eventData), pixallDataObject, options, metadata);
}
export function psxLearnMoreLinkClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    label: 'text-link::learn-more-about-private-seller-exchange',
    listingType: 'pvt',
    pageId: 'fyc_psx_vdp',
    pageType: 'vdp',
    program: 'DealerDotCom',
    vehicleType: 'used',
    ...pixallInventoryTransFormer.getInventoryData(inventory),
    ...data
  };
  const googleDataObject = {
    event: 'analytics_event',
    eventElement: 'private-seller',
    eventAction: 'click',
    eventResult: 'text-link::learn-more-about-private-seller-exchange',
    eventNonInteractive: 'false',
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      },
      ...transformProductConfig(inventory)
    }
  };
  return sendPSXLinkClickEvent(googleDataObject, pixallDataObject, options, metadata);
}
export function psxSellYourCarLinkClick(metadata, options) {
  const {
    data = {},
    state,
    dataLayer = {}
  } = metadata;
  const {
    walletData = {}
  } = data;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const {
    paymentsInfo,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', price, offerPrice);
  const {
    monthlyPayment,
    termLength,
    downPayment
  } = dealMetadata;
  const {
    type,
    condition = null,
    payoff = null,
    totalValue
  } = tradeinMetadata;
  const filteredDealMetadata = {
    monthlyPayment,
    termLength,
    downPayment
  };
  const filteredTradeInData = {
    type,
    condition,
    payoff,
    totalValue
  };
  const vehicleData = pixallInventoryTransFormer.getInventoryData(inventory);
  delete vehicleData?.listingCode;
  delete vehicleData?.chromeStyleId;
  delete vehicleData?.bodyStyle;
  const pixallDataObject = {
    eventSource: 'link',
    eventType: 'click',
    feature: 'psx',
    fuelEconomy: inventory?.specifications?.mpg?.value,
    label: 'text-link::private-seller-exchange::nds',
    listingType: 'pvt',
    pageId: 'fyc_psx_vdp',
    pageType: 'vdp',
    program: 'DealerDotCom',
    dealMetadata: filteredDealMetadata,
    tradeinMetadata: filteredTradeInData,
    ...vehicleData,
    ...data
  };
  const ga360Obj = {
    event: 'analytics_event',
    eventElement: 'nds',
    eventAction: 'click',
    eventResult: 'text-link::private-seller-exchange::nds',
    eventNonInteractive: false,
    eventData: {
      vehicle: {
        ...googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        }),
        clickType: 'pvt'
      }
    }
  };
  return sendPSXLinkClickEvent(ga360Obj, pixallDataObject, options, metadata);
}
export default {
  psxAnalyticsEvent,
  psxVideoStart,
  psxVideoComplete,
  psxPrivateSellerCarsClick,
  psxSellMyCarLinkClick,
  psxPreQualifyClick,
  psxWarrantyClick,
  psxEscrowClick,
  psxAutoGapInsuranceClick,
  psxVehicleProtectionClick,
  psxBuyerGuideClick,
  psxSellerGuideClick,
  psxFAQsClick,
  psxStripeClick,
  psxPrivateSellerExchangeLogoClick,
  psxVDPPageLoadEvent,
  psxVDPBuyNowClick,
  psxVDPTestDriveClick,
  psxVDPChatSellerClick,
  psxMakeOfferClick,
  psxVDPGetPreQualifyClick,
  psxVDPBuyerGuideClick,
  psxVDPEmailSubscribeClick,
  psxVDPVHRClick,
  psxVDPVHRImpression,
  psxVDPOptionDetailsAccordionClick,
  psxVDPStandardEquipmentAccordionClick,
  psxVDPOtherEquipmentAccordionClick,
  psxVDPEstimateYourMonthlyPaymentClick,
  psxVDPVehicleProtectionVehicleServiceContractsClick,
  psxVDPVehicleProtectionGAPProtectionClick,
  psxVDPFaqClick,
  psxLearnMoreFaqsClick,
  psxLearnMoreBuyersGuideClick,
  psxLearnMoreSellersGuideClick,
  psxVDPFinanceClick,
  psxVDPVehicleServiceContractsClick,
  psxVDPGapInsuranceClick,
  psxVDPPopoverCtaClick,
  psxBLPFinanceClick,
  psxBLPVehicleServiceContractsClick,
  psxBLPGapInsuranceClick,
  psxVDPEstimateDeliveryCostZipSubmit,
  psxVDPMyDashboardClick,
  psxOptionAddOnProtectionsImpression,
  psxOptionAddOnProtectionsClick,
  psxManufactureWarrantyImpression,
  psxManufactureWarrantyClick,
  psxEmailUsClick,
  psxLearnMoreLinkClick,
  psxSellYourCarLinkClick
};